import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { adminApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { can } from '../components/PaymentActions';

const usd = (n) => `$${Number(n || 0).toFixed(2)}`;

const CLASS_BADGE = {
  READY: { label: 'Ready', cls: 'bg-green-100 text-green-800' },
  PENDING_SHIPPO: { label: 'Pending (Shippo)', cls: 'bg-amber-100 text-amber-800' },
  ALREADY_REFUNDED: { label: 'Already refunded', cls: 'bg-gray-200 text-gray-700' },
  NOT_ELIGIBLE: { label: 'Not eligible', cls: 'bg-red-100 text-red-800' },
  ERROR: { label: 'Error', cls: 'bg-red-100 text-red-800' },
};

const KpiCard = ({ title, value, sub, tone }) => (
  <div className={`bg-white rounded-lg shadow p-4 border-l-4 ${tone}`}>
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
    <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
    {sub != null && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

const ClassBadge = ({ c }) => {
  const b = CLASS_BADGE[c] || CLASS_BADGE.NOT_ELIGIBLE;
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${b.cls}`}>{b.label}</span>;
};

const AccountFlag = ({ flag }) =>
  flag ? <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800" title="Account is on hold / suspended — refunding may not be appropriate">{flag === 'suspended' ? 'Suspended' : 'On hold'}</span> : null;

// Void-the-label-then-refund modal. CONFIRM-to-authorize (money-out). The amount defaults
// to the server's suggested refund (the FULL refundable balance), overridable, bounded server-side.
const RefundVoidModal = ({ item, onClose, onDone }) => {
  const [amount, setAmount] = useState((item.defaultRefundUsd ?? item.refundableUsd ?? 0).toFixed ? (item.defaultRefundUsd ?? item.refundableUsd ?? 0) : Number(item.defaultRefundUsd || item.refundableUsd || 0));
  const [reason, setReason] = useState('requested_by_customer');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionId] = useState(() =>
    (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const ready = typed.trim().toUpperCase() === 'CONFIRM';

  const submit = async () => {
    setBusy(true);
    try {
      const res = await adminApi.refundWithVoid(item.orderId, { amount: Number(amount), reason, actionId });
      const d = res.data || {};
      if (res.status === 202) toast.info(d.message || 'Shippo void in progress — refund will complete once confirmed.');
      else toast.success(`Refunded ${usd(d.amountRefundedUsd)} for ${item.orderId} (label voided)${d.customerNotified ? ' · customer emailed' : ' · email not sent'}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Void + refund failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-gray-600 bg-opacity-75 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mt-10 sm:mt-20 p-5">
        <h3 className="text-lg font-bold text-gray-900">Void + Refund — {item.orderId}</h3>
        {item.email && <p className="text-sm text-gray-500 mt-0.5 break-all">{item.email}</p>}
        <AccountFlag flag={item.accountFlag} />
        <p className="text-xs text-gray-400 mt-2">
          Captured {usd(item.capturedUsd)} · refundable {usd(item.refundableUsd)} ·
          Shippo label {item.shippoTransactionStatus || '—'}{item.shippoRefundStatus ? ` / refund ${item.shippoRefundStatus}` : ''}
        </p>
        <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
          This <strong>voids the carrier label at Shippo first</strong>, then refunds the customer in Stripe only after
          Shippo confirms. If the label was used, Shippo rejects the void and no money moves.
        </div>

        <label className="block mt-4 text-sm">
          <span className="text-gray-700">Refund amount (USD)</span>
          <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 min-h-[44px]" />
          <span className="text-xs text-gray-400">Default = full refundable balance. We initiated this cancellation, so no processing fee is deducted.</span>
        </label>
        <label className="block mt-3 text-sm">
          <span className="text-gray-700">Reason</span>
          <select value={reason} onChange={(e) => setReason(e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 min-h-[44px]">
            <option value="requested_by_customer">Requested by customer</option>
            <option value="duplicate">Duplicate</option>
            <option value="fraudulent">Fraudulent</option>
          </select>
        </label>
        <label className="block mt-3 text-sm">
          <span className="text-gray-700">Type <strong>CONFIRM</strong> to authorize this refund</span>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="CONFIRM"
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 min-h-[44px] uppercase" />
        </label>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} disabled={busy}
            className="min-h-[44px] px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={!ready || busy}
            className="min-h-[44px] px-4 py-2 rounded-md text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40">
            {busy ? 'Working…' : 'Void + Refund'}
          </button>
        </div>
      </div>
    </div>
  );
};

const RefundsReady = () => {
  const { user } = useAuth();
  const role = user?.role || 'viewer';
  const isFinance = can(role, 'finance');
  const [days, setDays] = useState(365);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalItem, setModalItem] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true); setError(null);
    try {
      const res = await adminApi.getRefundsReady({ days: d });
      setData(res.data);
    } catch (e) {
      const msg = e.response?.status === 403
        ? 'This report requires the finance role.'
        : (e.response?.data?.message || e.message || 'Failed to load refund worklist');
      setError(msg);
      if (e.response?.status !== 403) toast.error(msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isFinance) load(days); else setError('This report requires the finance role.'); }, [days, load, isFinance]);

  const s = data?.summary;
  const items = data?.items || [];
  const onDone = () => { setModalItem(null); load(days); };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Refunds — Ready for Refund</h1>
          <p className="text-sm text-gray-500">Cancelled / refund-requested orders, with live Shippo + Stripe status · your role: <span className="font-medium">{role}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Window</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="border border-gray-300 rounded-md text-sm px-3 py-2 min-h-[44px]">
            {[90, 180, 365, 730].map((d) => <option key={d} value={d}>{d} days</option>)}
          </select>
          <button onClick={() => load(days)} disabled={loading || !isFinance}
            className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            <ArrowPathIcon className={`h-5 w-5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <KpiCard title="Ready" value={s.READY} sub={`${usd(data.readyRefundUsd)} owed (full refund)`} tone="border-green-500" />
          <KpiCard title="Pending at Shippo" value={s.PENDING_SHIPPO} sub="void in flight — recheck later" tone="border-amber-400" />
          <KpiCard title="Already refunded" value={s.ALREADY_REFUNDED} sub="reconcile only — do not re-refund" tone="border-gray-400" />
          <KpiCard title="Not eligible / error" value={(s.NOT_ELIGIBLE || 0) + (s.ERROR || 0)} sub="used label, uncaptured, dispute…" tone="border-red-500" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4 mb-4 flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" /><span className="text-sm">{error}</span>
        </div>
      )}

      {loading && !data && <div className="text-center text-gray-500 py-12">Loading…</div>}

      {data && items.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow p-10 text-center text-gray-500">
          ✅ No refund-requested orders in the last {days} days. Scanned {data.window?.scanned} order(s).
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Order', 'Customer', 'Status', 'Shippo (label / refund)', 'Refundable', 'Default refund', 'Action'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((it) => (
                  <tr key={it.orderId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{it.orderId}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 break-all">{it.email}<AccountFlag flag={it.accountFlag} /></td>
                    <td className="px-4 py-3"><ClassBadge c={it.classification} /><div className="text-xs text-gray-400 mt-0.5">{it.reason}</div></td>
                    <td className="px-4 py-3 text-sm text-gray-600">{it.shippoTransactionStatus || '—'}{it.shippoRefundStatus ? ` / ${it.shippoRefundStatus}` : ''}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{it.refundableUsd != null ? usd(it.refundableUsd) : '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{it.defaultRefundUsd != null ? usd(it.defaultRefundUsd) : '—'}</td>
                    <td className="px-4 py-3">
                      {it.classification === 'READY' && isFinance
                        ? <button onClick={() => setModalItem(it)} className="min-h-[40px] px-3 py-1.5 rounded text-xs font-semibold text-white bg-red-600 hover:bg-red-700">Void + Refund</button>
                        : <span className="text-xs text-gray-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {items.map((it) => (
              <div key={it.orderId} className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">{it.orderId}</span><ClassBadge c={it.classification} />
                </div>
                <p className="text-sm text-gray-600 break-all mt-1">{it.email}<AccountFlag flag={it.accountFlag} /></p>
                <p className="text-xs text-gray-400 mt-1">{it.reason}</p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  <div><span className="text-gray-400">Shippo</span><br />{it.shippoTransactionStatus || '—'}{it.shippoRefundStatus ? ` / ${it.shippoRefundStatus}` : ''}</div>
                  <div><span className="text-gray-400">Refundable</span><br />{it.refundableUsd != null ? usd(it.refundableUsd) : '—'}</div>
                  <div><span className="text-gray-400">Default refund</span><br />{it.defaultRefundUsd != null ? usd(it.defaultRefundUsd) : '—'}</div>
                </div>
                {it.classification === 'READY' && isFinance && (
                  <div className="mt-3"><button onClick={() => setModalItem(it)} className="w-full min-h-[44px] px-3 py-2 rounded text-sm font-semibold text-white bg-red-600 hover:bg-red-700">Void + Refund</button></div>
                )}
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-400 mt-3">
            Scanned {data.window?.scanned} refund-requested order(s) in {days}d. Refund voids the carrier label first, then refunds the customer only after Shippo confirms SUCCESS.
          </p>
        </>
      )}

      {modalItem && <RefundVoidModal item={modalItem} onClose={() => setModalItem(null)} onDone={onDone} />}
    </div>
  );
};

export default RefundsReady;
